import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Search, RefreshCw, Settings2, Plus, Edit, Eye, Printer, 
  Upload, MoreHorizontal, Filter, ChevronDown, ArrowUpDown,
  FileDown, Trash2, Check, X, Send
} from 'lucide-react';

/**
 * Enterprise Data Table Component
 * Reusable table with toolbar, search, filters, and actions
 */
export function EnterpriseToolbar({
  title,
  totalCount,
  searchTerm,
  onSearchChange,
  onRefresh,
  onAdd,
  onEdit,
  onView,
  onPrint,
  onExport,
  onDelete,
  selectedCount = 0,
  customActions = [],
  addLabel = "Add",
  showAdvancedSearch = true,
  onAdvancedSearch
}) {
  return (
    <div className="bg-white border-b">
      {/* Header Row - Ultra Compact */}
      <div className="flex items-center justify-between px-2 py-1 border-b bg-slate-50">
        <div className="flex items-center gap-1.5">
          <h1 className="text-sm font-semibold text-slate-800">{title}</h1>
          <Badge variant="secondary" className="bg-blue-100 text-blue-700 font-mono text-[10px] px-1.5 py-0">
            {totalCount}
          </Badge>
        </div>
        {showAdvancedSearch && (
          <Button 
            variant="link" 
            className="text-blue-600 hover:text-blue-800 text-[10px] h-5 px-1"
            onClick={onAdvancedSearch}
          >
            Advanced Search
          </Button>
        )}
      </div>
      
      {/* Toolbar Row - Ultra Compact */}
      <div className="flex items-center justify-between px-2 py-1 gap-1">
        <div className="flex items-center gap-0.5">
          {/* Refresh */}
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-6 w-6 p-0"
            onClick={onRefresh}
            title="Refresh"
          >
            <RefreshCw className="h-3 w-3" />
          </Button>
          
          {/* Settings/Columns */}
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" title="Column Settings">
            <Settings2 className="h-3 w-3" />
          </Button>
          
          <div className="w-px h-4 bg-slate-200" />
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-1.5 top-1/2 transform -translate-y-1/2 h-3 w-3 text-slate-400" />
            <Input
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-6 h-6 w-40 text-xs bg-slate-50 border-slate-200"
            />
          </div>
          
          <div className="w-px h-4 bg-slate-200" />
          
          {/* Primary Actions */}
          {onAdd && (
            <Button 
              size="sm" 
              className="h-6 text-[10px] px-2 bg-emerald-600 hover:bg-emerald-700"
              onClick={onAdd}
            >
              <Plus className="h-3 w-3 mr-0.5" />
              {addLabel}
            </Button>
          )}
          
          {onEdit && (
            <Button 
              variant="outline" 
              size="sm" 
              className="h-6 text-[10px] px-2"
              onClick={onEdit}
              disabled={selectedCount !== 1}
            >
              <Edit className="h-3 w-3 mr-0.5" />
              Edit
            </Button>
          )}
          
          {onView && (
            <Button 
              variant="outline" 
              size="sm" 
              className="h-6 text-[10px] px-2"
              onClick={onView}
              disabled={selectedCount !== 1}
            >
              <Eye className="h-3 w-3 mr-0.5" />
              View
            </Button>
          )}
          
          <div className="w-px h-4 bg-slate-200" />
          
          {/* Secondary Actions */}
          {onPrint && (
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onPrint} title="Print">
              <Printer className="h-3 w-3" />
            </Button>
          )}
          
          {onExport && (
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onExport} title="Export">
              <FileDown className="h-3 w-3" />
            </Button>
          )}
          
          {/* Actions Dropdown */}
          {(customActions.length > 0 || onDelete) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-6 text-[10px] px-2">
                  Actions
                  <ChevronDown className="h-2.5 w-2.5 ml-0.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {customActions.map((action, idx) => (
                  <DropdownMenuItem key={idx} onClick={action.onClick} disabled={action.disabled} className="text-[10px] py-1">
                    {action.icon && <action.icon className="h-3 w-3 mr-1.5" />}
                    {action.label}
                  </DropdownMenuItem>
                ))}
                {onDelete && (
                  <DropdownMenuItem 
                    onClick={onDelete} 
                    disabled={selectedCount === 0}
                    className="text-red-600 text-[10px] py-1"
                  >
                    <Trash2 className="h-3 w-3 mr-1.5" />
                    Delete Selected
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        
        {selectedCount > 0 && (
          <Badge variant="outline" className="bg-blue-50 text-blue-700 text-[10px] px-1.5 py-0">
            {selectedCount} selected
          </Badge>
        )}
      </div>
    </div>
  );
}

/**
 * Enterprise Table Header Cell with sorting and filtering
 */
export function EnterpriseTableHead({ 
  children, 
  sortable = false, 
  sortDirection = null,
  onSort,
  filterable = false,
  className = ""
}) {
  return (
    <TableHead className={`bg-slate-100 text-slate-600 font-medium text-[10px] uppercase tracking-wide py-1 px-1.5 ${className}`}>
      <div className="flex items-center gap-0">
        <span className="flex-1">{children}</span>
        {sortable && (
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-4 w-4 p-0 hover:bg-slate-200"
            onClick={onSort}
          >
            <ArrowUpDown className="h-2 w-2" />
          </Button>
        )}
        {filterable && (
          <Button variant="ghost" size="sm" className="h-4 w-4 p-0 hover:bg-slate-200">
            <Filter className="h-2 w-2" />
          </Button>
        )}
      </div>
    </TableHead>
  );
}

/**
 * Status Badge with enterprise colors
 */
export function StatusBadge({ status, variant = "default" }) {
  const statusConfig = {
    // Purchase Status
    draft: { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Draft' },
    pending: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Pending' },
    pending_approval: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Pending' },
    approved: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Approved' },
    rejected: { bg: 'bg-red-100', text: 'text-red-700', label: 'Rejected' },
    sent: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Sent' },
    partially_received: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Partial' },
    fully_received: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Received' },
    confirmed: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Confirmed' },
    cancelled: { bg: 'bg-red-100', text: 'text-red-700', label: 'Cancelled' },
    converted_to_po: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Converted' },
    // Payment Status
    paid: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Paid' },
    partially_paid: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Partial' },
    overdue: { bg: 'bg-red-100', text: 'text-red-700', label: 'Overdue' },
    // Priority
    low: { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Low' },
    normal: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Normal' },
    high: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'High' },
    urgent: { bg: 'bg-red-100', text: 'text-red-700', label: 'Urgent' },
    // Generic
    active: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Active' },
    inactive: { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Inactive' },
  };
  
  const config = statusConfig[status?.toLowerCase()] || { bg: 'bg-slate-100', text: 'text-slate-600', label: status };
  
  return (
    <span className={`inline-flex items-center px-1 py-0 rounded text-[9px] font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
}

/**
 * Empty State Component
 */
export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-6 px-2">
      {Icon && (
        <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center mb-2">
          <Icon className="h-5 w-5 text-slate-400" />
        </div>
      )}
      <h3 className="text-xs font-medium text-slate-800 mb-0.5">{title}</h3>
      <p className="text-[10px] text-slate-500 mb-2 text-center max-w-xs">{description}</p>
      {action}
    </div>
  );
}

/**
 * Row Selection Checkbox
 */
export function SelectCheckbox({ checked, onCheckedChange, indeterminate = false }) {
  return (
    <Checkbox 
      checked={indeterminate ? "indeterminate" : checked}
      onCheckedChange={onCheckedChange}
      className="border-slate-300 h-3 w-3"
    />
  );
}

/**
 * Action Buttons for table rows
 */
export function RowActions({ onView, onEdit, onDelete, customActions = [] }) {
  return (
    <div className="flex items-center gap-0">
      {onView && (
        <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={onView}>
          <Eye className="h-3 w-3 text-slate-500" />
        </Button>
      )}
      {onEdit && (
        <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={onEdit}>
          <Edit className="h-3 w-3 text-slate-500" />
        </Button>
      )}
      {customActions.map((action, idx) => (
        <Button 
          key={idx} 
          variant="ghost" 
          size="sm" 
          className="h-5 w-5 p-0" 
          onClick={action.onClick}
          title={action.title}
        >
          <action.icon className={`h-3 w-3 ${action.className || 'text-slate-500'}`} />
        </Button>
      ))}
      {onDelete && (
        <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={onDelete}>
          <Trash2 className="h-3 w-3 text-red-500" />
        </Button>
      )}
    </div>
  );
}

export default {
  EnterpriseToolbar,
  EnterpriseTableHead,
  StatusBadge,
  EmptyState,
  SelectCheckbox,
  RowActions
};
